import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Stack } from "@wato/ui";
import { useApi, useErrorMessage } from "../api/context";
import type { LegalDocumentView } from "../api/types";
import { allAccepted, LegalAcceptance, refKey } from "../pages/signup/legal-acceptance";

/**
 * Dashboard-wide re-acceptance gate (WP-418, FR-LEGAL-003). Blocks the workspace until every
 * `material` document version the user has not accepted is accepted; the server only lists a
 * document as pending when a material version was published, so a typo-fix version never
 * renders this screen. A failed read fails open (no lock-out on a flaky network).
 */
export function ReAcceptanceGate({
  workspaceId,
  children,
}: {
  readonly workspaceId: string;
  readonly children: ReactNode;
}) {
  const { t } = useI18n();
  const api = useApi();
  const errorMessage = useErrorMessage();
  const [pending, setPending] = useState<readonly LegalDocumentView[]>([]);
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .pendingLegalDocuments(workspaceId)
      .then((docs) => active && setPending(docs))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [api, workspaceId]);

  const toggle = useCallback((doc: LegalDocumentView) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (!next.delete(refKey(doc))) next.add(refKey(doc));
      return next;
    });
  }, []);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await api.acceptLegalDocuments(workspaceId, pending);
      setPending([]);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  if (pending.length === 0) return <>{children}</>;
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="mx-auto max-w-lg p-6"
      data-testid="re-acceptance-gate"
    >
      <Stack gap={4}>
        <h1 className="text-xl font-semibold">{t("dashboard.legal.gateTitle")}</h1>
        <p className="text-sm text-fg-muted">{t("dashboard.legal.gateIntro")}</p>
        {error && <Alert tone="error">{error}</Alert>}
        <LegalAcceptance documents={pending} checked={checked} onToggle={toggle} />
        <Button
          onClick={accept}
          loading={busy}
          disabled={!allAccepted(pending, checked)}
          data-testid="re-acceptance-accept"
        >
          {t("dashboard.legal.gateAccept")}
        </Button>
      </Stack>
    </div>
  );
}
